/**
 * The Neosocial voice interface: one contract for every backend.
 *
 * Ported from spike PR #4 (`spikes/voice-layer/web/voice.d.ts`, branch
 * origin/claude/brave-austin-a6affa) and adapted to phase 1:
 * - signalling is a WebSocket at `/voice/ws` (not POST /join + a data
 *   channel); the first message is `hello` with the JWT from
 *   `POST /api/v1/voice/token` and the SDP offer;
 * - engine events are the protocol's `VoiceServerMessage` (member, left,
 *   slot, mute, moved, denied...);
 * - up to `VOICE_RECEIVE_SLOTS` (32) receive slots, offered once;
 * - listener mute (`local_mute`) tells the engine to stop sending a pid.
 *
 * The browser backend is `webVoice.ts`. A future native core implements
 * the same calls and events, so the screens never know which one runs.
 */
import type {
  NoiseSuppression,
  Pid,
  Snowflake,
  VoiceClientMessage,
  VoiceMediaClientMessage,
  VoiceMediaServerMessage,
  VoiceMediaSource,
  VoiceMember,
  VoiceMemberMedia,
  VoicePublishCaps,
  VoiceServerMessage,
  VoiceVideoRef,
} from '@neosocial/protocol';

/**
 * Every message comms-voice sends, screen share and video included
 * (docs/decisions/2026-10-29-screen-share-and-video.md §4.8; an
 * intersection that compiles before and after the protocol wires them in).
 */
export type ServerMessage = VoiceServerMessage | VoiceMediaServerMessage;
/** Every message the client sends comms-voice. */
export type ClientMessage = VoiceClientMessage | VoiceMediaClientMessage;
/** A roster entry with its live media (`media`, `welcome`). */
export type RosterMember = VoiceMember & VoiceMemberMedia;

export type MediaState = 'connecting' | 'connected' | 'failed' | 'closed';

export interface PeerLevel {
  slot: number;
  /** Who this slot carries now (from the last `slot` event). */
  pid: Pid;
  /** 0..1 decoded audio level. */
  level: number;
  packets: number;
  lost: number;
  /** Packets arrived since the last tick. */
  active: boolean;
}

/** Fired every `levelMs` while joined. */
export interface LevelsEvent {
  t: 'levels';
  /** Own input level 0..1 (0 while not transmitting). */
  me: number;
  /** Own input level before the transmit gate (for voice activity and the meter). */
  input: number;
  sent: number;
  /** Round trip to the engine in ms, null until measured. */
  rtt_ms: number | null;
  peers: PeerLevel[];
}

export interface StateEvent {
  t: 'state';
  state: MediaState;
}

/** The signalling socket closed (code from `VoiceCloseCodes`). */
export interface SignalClosedEvent {
  t: 'signal_closed';
  code: number;
  reason: string;
}

export type VoiceEvent = ServerMessage | LevelsEvent | StateEvent | SignalClosedEvent;
export type VoiceEventType = VoiceEvent['t'];
export type VoiceEventOf<T extends VoiceEventType> = Extract<VoiceEvent, { t: T }>;

export interface JoinOptions {
  /** The voice JWT. */
  token: string;
  /** Full WebSocket URL of the signalling endpoint. */
  url: string;
  iceServers?: RTCIceServer[];
  selfMute: boolean;
  selfDeaf: boolean;
  inputDeviceId?: string | null;
  outputDeviceId?: string | null;
  /** The master output volume, 0..2 (`setOutputVolume`); default 1. */
  outputVolume?: number;
  /** Noise suppression for the microphone (voice/noise.ts, 2026-11-01); default Standard. */
  noiseSuppression?: NoiseSuppression;
  /** The channel's password, when it has one (sent in `hello`; comms-voice checks it). */
  password?: string;
  /**
   * The most the microphone sends, in bit/s: the channel's bitrate (HV-B04)
   * or the console's cap, the lower (`sendBitrate`).
   */
  bitrate?: number | null;
}

export interface JoinResult {
  pid: Pid;
  channel: Snowflake;
  members: RosterMember[];
  join_ms: number;
  /** True when no microphone could be opened: joined to listen only. */
  listenOnly: boolean;
  /** The saved microphone is gone, so the system default one is used (HV-B07). */
  micFallback?: boolean;
}

/** What `setInputDevice` opened: `ok` when a microphone is live now. */
export interface MicResult {
  ok: boolean;
  /** The chosen device is gone, so the system default one is used. */
  fallback: boolean;
}

/**
 * A join that ended before `welcome`: the socket's close code and, when
 * comms-voice refused it, its `denied` reason (`full`, `not_allowed`...).
 */
export type JoinError = Error & { code?: number; reason?: string };

/** A publish overtaken by a newer publish or unpublish of its source: nothing to undo or say. */
export class MediaSupersededError extends Error {
  constructor() {
    super('superseded');
    this.name = 'MediaSupersededError';
  }
}

export class VoiceDeniedError extends Error {
  readonly denied = true;
  constructor(
    readonly reason: string,
    readonly event?: VoiceEventOf<'denied'>,
  ) {
    super(reason);
    this.name = 'VoiceDeniedError';
  }
}

export interface Voice {
  readonly kind: 'web' | 'native';
  /** Connects; resolves on `welcome` (the SDP answer is applied). */
  join(o: JoinOptions): Promise<JoinResult>;
  /** Resolves on `moved`; rejects with `VoiceDeniedError` on `denied`. */
  move(channel: Snowflake, password?: string): Promise<VoiceEventOf<'moved'>>;
  /** Self-mute: no packets leave, and others see "mic off". */
  mute(on: boolean): void;
  /** The push-to-talk / voice-activity gate: no packets while closed, nothing shown to others. */
  transmit(on: boolean): void;
  /**
   * Nobody may hear you now (machine.ts `myVoiceBlocked`: a server mute or
   * deafen, a channel mute or deafen here, or no Speak here): no packets
   * leave, as with self-mute, until it lifts; nothing is told to others
   * (comms-voice shows the mute itself). Bandwidth, 2026-10-29.
   */
  block(on: boolean): void;
  /** Stop playing received audio (and tell others). */
  deafen(on: boolean): void;
  /** Listener mute: the engine stops sending this pid to us (R1). */
  localMute(pid: Pid, muted: boolean): void;
  /** Per-person playback volume, 0..2 (MAX_USER_VOLUME); above 1 boosts through Web Audio. */
  setVolume(pid: Pid, volume: number): void;
  /**
   * The most the microphone sends, in bit/s (`sendBitrate`: the channel's
   * bitrate and the console's cap); null: no cap.
   */
  setBitrate(bps: number | null): void;
  /**
   * Switches the microphone mid-call (the system default when the device is
   * gone). Resolves null when not in a call.
   */
  setInputDevice(id: string | null): Promise<MicResult | null>;
  setOutputDevice(id: string | null): Promise<void>;
  /**
   * The master output volume, 0..2: each voice and stream sound plays at
   * its own volume times this (the product capped at 200 %). Optional, so
   * a backend without it plays everything at its own volume.
   */
  setOutputVolume?(volume: number): void;
  /**
   * Voice extras (2026-11-01), optional so a backend without them still
   * works. Noise suppression mid-call: the microphone opens again with it
   * (null when not in a call); resolves with what runs.
   */
  setNoiseSuppression?(mode: NoiseSuppression): Promise<NoiseSuppression | null>;
  /** The noise suppression that runs now (Strong that could not start runs as Standard). */
  readonly noiseSuppression?: NoiseSuppression;
  /** The priority-speaker key is held: `speaking` says `priority` while transmitting. */
  setPriority?(on: boolean): void;
  /** There was input (keys, pointer, touch): the next ping says so (the AFK timer). */
  noteInput?(): void;
  /**
   * Screen share and camera on this connection (`voiceMedia`); absent where
   * the backend carries no video (the native TLS fallback): publishing is
   * then `unsupported`, as comms-voice would say.
   */
  readonly media?: VoiceMedia;
  leave(): void;
  on<T extends VoiceEventType | '*'>(
    t: T,
    fn: (ev: T extends VoiceEventType ? VoiceEventOf<T> : VoiceEvent) => void,
  ): () => void;
  readonly state: MediaState;
  readonly pid: Pid | null;
  readonly channel: Snowflake | null;
}

/** A quality picked in place while live (the stream tile's menu), never above the caps. */
export interface StreamQuality {
  height: number;
  fps: number;
}

/**
 * Screen share (Go Live) and camera on a voice connection
 * (docs/decisions/2026-10-29-screen-share-and-video.md §4). The capture
 * tracks stay the caller's: these calls never stop them.
 */
export interface VoiceMedia {
  /**
   * `publish {on:true}`, then the paused send-only m-line(s) and an `sdp`.
   * Resolves with the publication's caps; rejects with `VoiceDeniedError`
   * (`feature_off`, `not_allowed`, `media_limit`, `unsupported`,
   * `unknown_request` from an older comms-voice). `quality`: a lower box
   * under the caps from the first frame (the share sheet's pick), as
   * `setQuality` would set it. An `unpublish` meanwhile, or sections that
   * never get there (a refused or lost `sdp`), leave nothing published:
   * comms-voice is told `publish {on:false}` and it rejects.
   */
  publish(
    source: VoiceMediaSource,
    tracks: { video: MediaStreamTrack; audio?: MediaStreamTrack | null },
    quality?: StreamQuality | null,
  ): Promise<VoicePublishCaps>;
  /** `publish {on:false}`, then the sections go (`sdp`). */
  unpublish(source: VoiceMediaSource): void;
  /** Watch or stop watching `pid`'s screen; resolves with the watch now (`watch_set`). */
  watch(pid: Pid, on: boolean): Promise<boolean>;
  /** The videos on your screen now, most important first (`visible`; `[]` when hidden). */
  setVisible(sources: VoiceVideoRef[]): void;
  /** What a video slot carries for `pid`'s `source` now, or null. */
  stream(pid: Pid, source: VoiceMediaSource): MediaStream | null;
  /**
   * A watched stream's own sound volume, 0..2 (`MAX_STREAM_VOLUME`; 0 is
   * muted), apart from the person's voice volume; above 1 boosts through
   * Web Audio, as a voice above 100 % does.
   */
  setStreamVolume(pid: Pid, volume: number): void;
  /** Lower (or restore) a live publication's quality in place, under its caps (no message). */
  setQuality(source: VoiceMediaSource, q: StreamQuality): void;
  /** The sources published now. */
  readonly publishing: VoiceMediaSource[];
}

/**
 * The most the microphone sends, in bit/s: the lowest of the caps given
 * (the channel's bitrate, the console's `limits.voice_bitrate_kbps` live and
 * as the voice token carried it). Missing, zero or bad values do not count;
 * null when none is left (no cap).
 */
export function sendBitrate(...caps: Array<number | null | undefined>): number | null {
  let out: number | null = null;
  for (const c of caps)
    if (typeof c === 'number' && Number.isFinite(c) && c > 0)
      out = out === null ? c : Math.min(out, c);
  return out;
}

/** Per-person volume goes up to 200 %. */
export const MAX_USER_VOLUME = 2;

/** Clamps a per-person volume to 0..MAX_USER_VOLUME (NaN counts as 1). */
export function clampVolume(v: number): number {
  if (!Number.isFinite(v)) return 1;
  return Math.max(0, Math.min(MAX_USER_VOLUME, v));
}

/** A stream's own sound goes up to 200 % too (Discord's stream volume). */
export const MAX_STREAM_VOLUME = 2;

/** Clamps a stream's volume to 0..MAX_STREAM_VOLUME (NaN counts as 1). */
export function clampStreamVolume(v: number): number {
  if (!Number.isFinite(v)) return 1;
  return Math.max(0, Math.min(MAX_STREAM_VOLUME, v));
}

/**
 * How one receive slot plays a volume: up to 100 % the audio element's own
 * volume; above it the element is muted (it must stay attached, or Chrome
 * stops feeding remote WebRTC audio to Web Audio) and a GainNode plays it.
 * Where the element's volume is fixed at 100 % (`elementVolume` false: iOS
 * Safari ignores it), anything below 100 % goes through the GainNode too.
 */
export function slotPlayback(
  volume: number,
  muted: boolean,
  elementVolume = true,
): { elementMuted: boolean; elementVolume: number; gain: number | null } {
  const v = clampVolume(volume);
  const viaGain = v > 1 || (!elementVolume && !muted && v > 0 && v < 1);
  if (!viaGain) return { elementMuted: muted || v === 0, elementVolume: v, gain: null };
  return { elementMuted: true, elementVolume: 1, gain: muted ? 0 : v };
}

let volumeWorks: boolean | undefined;

/**
 * Does setting an audio element's volume do anything here? iOS Safari keeps
 * it at 1 (the hardware buttons own the volume), so a slider below 100 %
 * would silently do nothing (owner, 2026-11-02). Asked once.
 */
export function elementVolumeWorks(): boolean {
  if (volumeWorks !== undefined) return volumeWorks;
  try {
    const a = new Audio();
    a.volume = 0.5;
    volumeWorks = a.volume === 0.5;
  } catch {
    volumeWorks = true;
  }
  return volumeWorks;
}
